'use client';

import { useState, useEffect, useCallback } from 'react';
import { useTranslations } from 'next-intl';
import { IoChevronBack, IoChevronForward } from 'react-icons/io5';
import type { DateInterval } from '@/types/calendar';
import { compareDates, isDateBetween, addDays } from '@/lib/calendar-utils';
import { MinStayNotice } from './min-stay-notice';

const FILTER_MONTH_KEYS = [
  'january', 'february', 'march', 'april', 'may', 'june',
  'july', 'august', 'september', 'october', 'november', 'december',
] as const;

export function FilterCalendar({
  interval,
  onIntervalChange,
  minStayByDate,
}: {
  interval: DateInterval;
  onIntervalChange: (interval: DateInterval) => void;
  /**
   * Global per-date min-stay (D30, doc 81 §12.4 D): `min` across all rooms of
   * each day's min-stay. Drives the festive auto-select on first click — a day
   * with min-stay N auto-fills check-out N nights out. Absent dates ⇒ 1.
   */
  minStayByDate?: Record<string, number>;
}) {
  const tc = useTranslations('common');
  const minStayFor = (date: string | null): number =>
    date && minStayByDate ? (minStayByDate[date] ?? 1) : 1;

  const now = new Date();
  // Start on the check-in month when a date is pre-selected (e.g. via URL
  // ?checkIn=YYYY-MM-DD); otherwise on the current month. Read once — once
  // the user navigates the calendar, the parent's interval changes shouldn't
  // yank the view back.
  const [year, setYear] = useState(() =>
    interval.checkIn ? parseInt(interval.checkIn.substring(0, 4)) : now.getFullYear(),
  );
  const [month, setMonth] = useState(() =>
    interval.checkIn ? parseInt(interval.checkIn.substring(5, 7)) : now.getMonth() + 1,
  );
  const [hoveredDate, setHoveredDate] = useState<string | null>(null);

  // Selection phase tracks the 3-click cycle on top of the 1-night-default.
  type SelectionPhase = 'idle' | 'one-night' | 'multi-night';
  const [phase, setPhase] = useState<SelectionPhase>(() => {
    if (interval.checkIn && interval.checkOut) return 'multi-night';
    if (interval.checkIn) return 'one-night';
    return 'idle';
  });

  // External clears snap phase back to idle.
  useEffect(() => {
    if (!interval.checkIn && !interval.checkOut) setPhase('idle');
  }, [interval.checkIn, interval.checkOut]);

  // Hover preview only renders during the "one extend chance" window.
  const selectionMode = phase === 'one-night' ? 'check-out' : 'check-in';
  const today = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;

  // Min-stay floor for the active check-in during check-out selection: earliest
  // valid check-out. Days strictly inside it are dimmed / not previewable.
  const minStayFloorDate =
    phase === 'one-night' && interval.checkIn && minStayFor(interval.checkIn) > 1
      ? addDays(interval.checkIn, minStayFor(interval.checkIn))
      : null;
  // Min-stay nights for the notice banner (selected or hovered check-in).
  const bannerMinStay = minStayFor(interval.checkIn ?? hoveredDate);

  const canGoPrev = () =>
    year > now.getFullYear() || (year === now.getFullYear() && month > now.getMonth() + 1);

  const goPrev = () => {
    if (month === 1) { setYear(y => y - 1); setMonth(12); }
    else setMonth(m => m - 1);
  };
  const goNext = () => {
    if (month === 12) { setYear(y => y + 1); setMonth(1); }
    else setMonth(m => m + 1);
  };

  // Booking.com-style 3-click cycle on top of a 1-night default:
  //   Click 1 → set check-in + check-out = check-in+1 (phase → one-night)
  //   Click 2 (later) → extend check-out (phase → multi-night)
  //   Click 2 (at/before) → restart with 1-night on clicked (stays one-night)
  //   Click 3 (any) → restart with 1-night on clicked (phase → one-night)
  const handleDayClick = useCallback((dateStr: string) => {
    const { checkIn } = interval;
    if (phase === 'one-night' && checkIn && compareDates(dateStr, checkIn) > 0) {
      // Snap a below-minimum pick up to checkIn + its min-stay (doc 81 §12.3).
      const minCheckOut = addDays(checkIn, minStayFor(checkIn));
      const target = compareDates(dateStr, minCheckOut) < 0 ? minCheckOut : dateStr;
      onIntervalChange({ checkIn, checkOut: target });
      setPhase('multi-night');
      return;
    }
    // First click: auto-fill check-out to the day's min-stay (1 night normally,
    // e.g. 3 hotel-wide on a festive day — D30).
    onIntervalChange({ checkIn: dateStr, checkOut: addDays(dateStr, minStayFor(dateStr)) });
    setPhase('one-night');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [interval, phase, onIntervalChange, minStayByDate]);

  const firstDayOfMonth = new Date(year, month - 1, 1);
  const startingDayOfWeek = (firstDayOfMonth.getDay() + 6) % 7;
  const daysInMonth = new Date(year, month, 0).getDate();

  const getDayState = (dateStr: string) => {
    const { checkIn, checkOut } = interval;
    if (checkIn && dateStr === checkIn) return 'check-in';
    if (checkOut && dateStr === checkOut) return 'check-out';
    if (checkIn && checkOut && isDateBetween(dateStr, checkIn, checkOut)) return 'in-range';
    if (
      selectionMode === 'check-out' && checkIn && hoveredDate &&
      compareDates(hoveredDate, checkIn) > 0 &&
      (!minStayFloorDate || compareDates(hoveredDate, minStayFloorDate) >= 0)
    ) {
      if (dateStr === hoveredDate || isDateBetween(dateStr, checkIn, hoveredDate)) return 'preview';
    }
    return 'normal';
  };

  return (
    <div>
      <MinStayNotice nights={bannerMinStay} tone="light" />
      {/* Month header */}
      <div className="flex justify-between items-center mb-1.5">
        <button
          className={`p-1 rounded-full transition-colors hover:bg-gray-100 ${!canGoPrev() ? 'opacity-0 pointer-events-none' : ''}`}
          onClick={canGoPrev() ? goPrev : undefined}
        >
          <IoChevronBack className="w-3.5 h-3.5 text-gray-500" />
        </button>
        <span className="text-sm font-semibold text-gray-700">
          {tc(`calendar.months.${FILTER_MONTH_KEYS[month - 1]}`)} {year}
        </span>
        <button
          className="p-1 rounded-full transition-colors hover:bg-gray-100"
          onClick={goNext}
        >
          <IoChevronForward className="w-3.5 h-3.5 text-gray-500" />
        </button>
      </div>

      {/* Day grid — no column gap for seamless range highlighting, small row gap */}
      <div className="grid grid-cols-7 gap-x-0 gap-y-1">
        {Array.from({ length: startingDayOfWeek }, (_, i) => (
          <div key={`e-${i}`} className="w-full aspect-square" />
        ))}
        {Array.from({ length: daysInMonth }, (_, i) => {
          const dayNum = i + 1;
          const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(dayNum).padStart(2, '0')}`;
          const isPast = compareDates(dateStr, today) < 0;
          const isToday = dateStr === today;
          const dayState = getDayState(dateStr);
          // Inside the min-stay floor (too short to be a check-out) → dim it.
          const belowMinStay =
            minStayFloorDate !== null &&
            dayState === 'normal' &&
            interval.checkIn !== null &&
            compareDates(dateStr, interval.checkIn) > 0 &&
            compareDates(dateStr, minStayFloorDate) < 0;

          return (
            <div
              key={dayNum}
              className={[
                'w-full aspect-square flex items-center justify-center text-sm px-1.5 transition-colors cursor-pointer',
                isPast && 'opacity-30 !cursor-not-allowed',
                belowMinStay && 'opacity-30',
                !isPast && dayState === 'normal' && 'hover:bg-gray-100 text-gray-700 rounded-sm',
                dayState === 'check-in' && 'bg-[#801515] text-white font-semibold rounded-l-md',
                dayState === 'check-out' && 'bg-[#801515] text-white font-semibold rounded-r-md',
                dayState === 'in-range' && 'bg-[#801515]/10 text-[#801515]',
                dayState === 'preview' && 'bg-[#801515]/5 text-gray-600',
                isToday && dayState === 'normal' && 'ring-1 ring-amber-500/50',
              ].filter(Boolean).join(' ')}
              onClick={!isPast ? () => handleDayClick(dateStr) : undefined}
              onMouseEnter={!isPast && selectionMode === 'check-out' && interval.checkIn ? () => setHoveredDate(dateStr) : undefined}
              onMouseLeave={selectionMode === 'check-out' ? () => setHoveredDate(null) : undefined}
            >
              {dayNum}
            </div>
          );
        })}
      </div>
    </div>
  );
}
